/**
 * NotFoundPage Component
 * Shown when the content API has no page for the requested URL.
 *
 * nginx already answers such a URL with HTTP 404 (see the route check in
 * nginx.conf), so this page only has to render; noindex covers in-app
 * navigation, where no new document — and so no status code — is involved.
 */

import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { getLocale } from "@/utils/locale-store";
import { localizeUrl } from "@/utils/url";
import { useLocalizedTexts, t } from "@/utils/translations";
import styles from "./NotFoundPage.module.css";

const ILLUSTRATION_SRC = "/images/not-found/lost-dog-misty-forest.svg";

/** React Router stores the history index in `history.state`; 0 = entry page. */
function hasInAppHistory(): boolean {
  const state = window.history.state as { idx?: number } | null;
  return (state?.idx ?? 0) > 0;
}

/**
 * The tab has a page before this one that is not another site, so the browser
 * can step back to it. That covers a full page load from one of our pages and
 * a URL typed into the address bar, which sends no referrer at all.
 */
function canStepBackInBrowser(): boolean {
  if (window.history.length <= 1) return false;
  if (!document.referrer) return true;
  try {
    return new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
}

export function NotFoundPage() {
  const texts = useLocalizedTexts();
  const navigate = useNavigate();
  const homeUrl = localizeUrl("/", getLocale().language);

  // Going back to Google (or nowhere, on a fresh tab) would drop the visitor
  // off the site, so then we go home instead.
  const handleBack = () => {
    if (hasInAppHistory()) {
      navigate(-1);
    } else if (canStepBackInBrowser()) {
      window.history.back();
    } else {
      navigate(homeUrl);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          {t(texts, "Tracker.NotFound.PageTitle", "Page not found")}
        </title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className={styles.page}>
        <div className={styles.illustration}>
          {/* Above the fold: loaded eagerly. The artwork is a 12 KB vector, so
              it stays sharp at every size without raster variants. */}
          <img
            src={ILLUSTRATION_SRC}
            alt={t(texts, "Tracker.NotFound.ImageAlt", "Misty forest")}
            width={800}
            height={1000}
            loading="eager"
            decoding="async"
            className={styles.image}
          />
        </div>

        <div className={styles.content}>
          <h1 className={styles.title}>
            {t(
              texts,
              "Tracker.NotFound.Headline",
              "I think we are lost at the moment."
            )}
          </h1>
          <p className={styles.body}>
            {t(
              texts,
              "Tracker.NotFound.Body",
              "The page you're looking for doesn't exist. It may have been moved or removed."
            )}
          </p>

          <div className={styles.actions}>
            <Link to={homeUrl} className={styles.primaryButton}>
              {t(texts, "Tracker.NotFound.CtaHome", "Go to starting point")}
            </Link>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={handleBack}
            >
              {t(
                texts,
                "Tracker.NotFound.CtaBack",
                "Get back where you came from"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
